import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FaUsers, FaEye, FaFileInvoiceDollar, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import Spinner from '../components/layout/Spinner';
import { dashboardService } from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    customerCount: 0,
    prescriptionCount: 0,
    invoiceCount: 0,
    pendingInvoiceCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          customers,
          prescriptions,
          invoices,
          pendingInvoices
        ] = await Promise.all([
          dashboardService.getCustomers(),
          dashboardService.getPrescriptions(),
          dashboardService.getInvoices(),
          dashboardService.getPendingInvoices()
        ]);

        setStats({
          customerCount: customers.pagination.total,
          prescriptionCount: prescriptions.pagination.total,
          invoiceCount: invoices.pagination.total,
          pendingInvoiceCount: pendingInvoices.pagination.total
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <Container>
      <h1 className="mb-4">Dashboard</h1>
      
      {error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <Row className="mb-4">
            <Col md={6} lg={3} className="mb-3">
              <Link to="/customers" className="text-decoration-none">
                <div className="dashboard-card dashboard-card-customers">
                  <div className="dashboard-card-icon">
                    <FaUsers />
                  </div>
                  <div className="dashboard-card-title">Total Customers</div>
                  <div className="dashboard-card-value">{stats.customerCount}</div>
                </div>
              </Link>
            </Col>
            
            <Col md={6} lg={3} className="mb-3">
              <Link to="/prescriptions" className="text-decoration-none">
                <div className="dashboard-card dashboard-card-prescriptions">
                  <div className="dashboard-card-icon">
                    <FaEye />
                  </div>
                  <div className="dashboard-card-title">Total Prescriptions</div>
                  <div className="dashboard-card-value">{stats.prescriptionCount}</div>
                </div>
              </Link>
            </Col>
            
            <Col md={6} lg={3} className="mb-3">
              <Link to="/invoices" className="text-decoration-none">
                <div className="dashboard-card dashboard-card-invoices">
                  <div className="dashboard-card-icon">
                    <FaFileInvoiceDollar />
                  </div>
                  <div className="dashboard-card-title">Total Invoices</div>
                  <div className="dashboard-card-value">{stats.invoiceCount}</div>
                </div>
              </Link>
            </Col>
            
            <Col md={6} lg={3} className="mb-3">
              <Link to="/invoices?status=pending" className="text-decoration-none">
                <div className="dashboard-card dashboard-card-pending">
                  <div className="dashboard-card-icon">
                    <FaExclamationTriangle />
                  </div>
                  <div className="dashboard-card-title">Pending Invoices</div>
                  <div className="dashboard-card-value">{stats.pendingInvoiceCount}</div>
                </div>
              </Link>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>Recent Customers</Card.Header>
                <Card.Body>
                  <p className="text-muted">
                    {stats.customerCount > 0 ? (
                      'View all customers to see the complete list.'
                    ) : (
                      'No customers found. Add your first customer to get started.'
                    )}
                  </p>
                  <Link to="/customers" className="btn btn-primary">
                    View All Customers
                  </Link>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>Recent Invoices</Card.Header>
                <Card.Body>
                  <p className="text-muted">
                    {stats.invoiceCount > 0 ? (
                      'View all invoices to see the complete list.'
                    ) : (
                      'No invoices found. Create your first invoice to get started.'
                    )}
                  </p>
                  <Link to="/invoices" className="btn btn-primary">
                    View All Invoices
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <Row>
            <Col md={12}>
              <Card>
                <Card.Header>Welcome, {user && user.name}</Card.Header>
                <Card.Body>
                  <p>
                    Welcome to the Optical Data Management System. This dashboard provides an overview of your optical business data.
                  </p>
                  <p>
                    Use the navigation menu to manage customers, prescriptions, and invoices.
                  </p>
                  {user && user.role === 'admin' && (
                    <p>
                      As an administrator, you also have access to user management features.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default Dashboard;
